import { useRouter } from '@uirouter/react';
import { Play, RefreshCw, Slash, Square, Trash2 } from 'lucide-react';

import * as notifications from '@/portainer/services/notifications';
import { confirmContainerDeletion } from '@/react/docker/containers/common/confirm-container-delete-modal';
import { setPortainerAgentTargetHeader } from '@/portainer/services/http-request.helper';
import {
  ContainerId,
  ContainerStatus,
  ContainerListViewModel,
} from '@/react/docker/containers/types';
import type { EnvironmentId } from '@/react/portainer/environments/types';
import { Authorized } from '@/react/hooks/useUser';

import { ButtonGroup, Button } from '@@/buttons';

import {
  killContainer,
  removeContainer,
  restartContainer,
  startContainer,
  stopContainer,
} from '../async-containers.service';

type ContainerServiceAction = (
  endpointId: EnvironmentId,
  containerId: ContainerId
) => Promise<void>;

interface Props {
  selectedItems: ContainerListViewModel[];
  endpointId: EnvironmentId;
}

export function ContainersDatatableActions({
  selectedItems,
  endpointId,
}: Props) {
  const selectedItemCount = selectedItems.length;
  const hasStoppedItemsSelected = selectedItems.some((item) =>
    [
      ContainerStatus.Stopped,
      ContainerStatus.Created,
      ContainerStatus.Exited,
    ].includes(item.Status)
  );
  const hasRunningItemsSelected = selectedItems.some((item) =>
    [
      ContainerStatus.Running,
      ContainerStatus.Healthy,
      ContainerStatus.Unhealthy,
      ContainerStatus.Starting,
    ].includes(item.Status)
  );

  const router = useRouter();

  return (
    <ButtonGroup>
      <Authorized authorizations="DockerContainerStart">
        <Button
          color="light"
          data-cy="edge-device-containers-start-button"
          onClick={() => onStartClick(selectedItems)}
          disabled={selectedItemCount === 0 || !hasStoppedItemsSelected}
          icon={Play}
        >
          Start
        </Button>
      </Authorized>

      <Authorized authorizations="DockerContainerStop">
        <Button
          color="light"
          data-cy="edge-device-containers-stop-button"
          onClick={() => onStopClick(selectedItems)}
          disabled={selectedItemCount === 0 || !hasRunningItemsSelected}
          icon={Square}
        >
          Stop
        </Button>
      </Authorized>

      <Authorized authorizations="DockerContainerKill">
        <Button
          color="light"
          data-cy="edge-device-containers-kill-button"
          onClick={() => onKillClick(selectedItems)}
          disabled={selectedItemCount === 0 || hasStoppedItemsSelected}
          icon={Slash}
        >
          Kill
        </Button>
      </Authorized>

      <Authorized authorizations="DockerContainerRestart">
        <Button
          color="light"
          data-cy="edge-device-containers-restart-button"
          onClick={() => onRestartClick(selectedItems)}
          disabled={selectedItemCount === 0}
          icon={RefreshCw}
        >
          Restart
        </Button>
      </Authorized>

      <Authorized authorizations="DockerContainerDelete">
        <Button
          color="dangerlight"
          data-cy="edge-device-containers-remove-button"
          onClick={() => onRemoveClick(selectedItems)}
          disabled={selectedItemCount === 0}
          icon={Trash2}
        >
          Remove
        </Button>
      </Authorized>
    </ButtonGroup>
  );

  function onStartClick(selectedItems: ContainerListViewModel[]) {
    const successMessage = 'Container successfully started';
    const errorMessage = 'Unable to start container';
    executeActionOnContainerList(
      selectedItems,
      startContainer,
      successMessage,
      errorMessage
    );
  }

  function onStopClick(selectedItems: ContainerListViewModel[]) {
    const successMessage = 'Container successfully stopped';
    const errorMessage = 'Unable to stop container';
    executeActionOnContainerList(
      selectedItems,
      stopContainer,
      successMessage,
      errorMessage
    );
  }

  function onRestartClick(selectedItems: ContainerListViewModel[]) {
    const successMessage = 'Container successfully restarted';
    const errorMessage = 'Unable to restart container';
    executeActionOnContainerList(
      selectedItems,
      restartContainer,
      successMessage,
      errorMessage
    );
  }

  function onKillClick(selectedItems: ContainerListViewModel[]) {
    const successMessage = 'Container successfully killed';
    const errorMessage = 'Unable to kill container';
    executeActionOnContainerList(
      selectedItems,
      killContainer,
      successMessage,
      errorMessage
    );
  }

  async function onRemoveClick(selectedItems: ContainerListViewModel[]) {
    const isOneContainerRunning = selectedItems.some(
      (container) => container.State === 'running'
    );

    const runningTitle = isOneContainerRunning ? 'running' : '';
    const title = `You are about to remove one or more ${runningTitle} containers.`;

    const result = await confirmContainerDeletion(title);
    if (!result) {
      return;
    }
    const { removeVolumes } = result;

    removeSelectedContainers(selectedItems, removeVolumes);
  }

  async function executeActionOnContainerList(
    containers: ContainerListViewModel[],
    action: ContainerServiceAction,
    successMessage: string,
    errorMessage: string
  ) {
    for (let i = 0; i < containers.length; i += 1) {
      const container = containers[i];
      try {
        setPortainerAgentTargetHeader(container.NodeName);
        await action(endpointId, container.Id);
        notifications.success(successMessage, container.Names[0]);
      } catch (err) {
        notifications.error(
          'Failure',
          err as Error,
          `${errorMessage}:${container.Names[0]}`
        );
      }
    }

    router.stateService.reload();
  }

  async function removeSelectedContainers(
    containers: ContainerListViewModel[],
    cleanVolumes: boolean
  ) {
    for (let i = 0; i < containers.length; i += 1) {
      const container = containers[i];
      try {
        setPortainerAgentTargetHeader(container.NodeName);
        await removeContainer(endpointId, container, cleanVolumes);
        notifications.success(
          'Container removal successfully planned',
          container.Names[0]
        );
      } catch (err) {
        notifications.error(
          'Failure',
          err as Error,
          'Unable to schedule container removal'
        );
      }
    }

    router.stateService.reload();
  }
}
